import React, { useState, useEffect } from 'react';
import Tooltip from './Tooltip';
import { GPX_PRESETS } from '../constants/gpxPresets';

const styles = {
  fixedSelectionBar: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    zIndex: 9999,
    backgroundColor: '#f57c00',
    color: '#fff',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    boxSizing: 'border-box',
  },
  spacer: {
    height: '54px',
    display: 'block',
  },
  container: {
    padding: '16px',
    backgroundColor: '#fffdfa',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    border: '1px solid #ffe0b2',
    position: 'relative',
    boxSizing: 'border-box',
    width: '100%',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
    gap: '16px',
    padding: 0,
    margin: '16px 0 0 0',
    listStyle: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: '#f8f2ee',
    border: '2px solid #cbd5e1',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  badgeBase: {
    position: 'absolute',
    top: '0',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    padding: '2px 8px',
    borderBottomRightRadius: '10px',
    borderBottomLeftRadius: '10px',
    zIndex: 1,
    whiteSpace: 'nowrap'
  },
  specs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: 'auto',
    paddingTop: '12px',
    borderTop: '1px solid rgba(0,0,0,0.05)',
  },
  specItem: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    border: '1px solid #f1f5f9',
  }
};

/* km-effort 計算 */
const getKmEffort = (distance, elevation) => {
  return distance + elevation / 100;
};

/* ITRA分類（英字 + 日本語併記） */
const getCategoryHybrid = (distance, elevation) => {
  const effort = getKmEffort(distance, elevation);

  if (effort <= 24) return { code: "XXS", jp: "超初級" };
  if (effort <= 44) return { code: "XS", jp: "初級" };
  if (effort <= 74) return { code: "S", jp: "中級" };
  if (effort <= 114) return { code: "M", jp: "中上級" };
  if (effort <= 154) return { code: "L", jp: "上級" };
  if (effort <= 209) return { code: "XL", jp: "超上級" };
  return { code: "XXL", jp: "ウルトラ級" };
};

/* カテゴリバッジの背景色（3分類） */
const getCategoryBadgeColor = (code, isSelected) => {
  const isBlue = code === "XXS" || code === "XS";
  const isGreen = code === "S" || code === "M" || code === "L";
  const isRed = code === "XL" || code === "XXL";

  if (!isSelected) {
    if (isBlue) return "#90caf9";   // 薄い青
    if (isGreen) return "#66bb6a";  // 薄い緑
    if (isRed) return "#ef9a9a";    // 薄い赤
  }

  if (isBlue) return "#42a5f5";     // 濃い青
  if (isGreen) return "#009b2f";    // 濃い緑
  if (isRed) return "#e53935";      // 濃い赤

  return "#ccc";
};


function GPSImportWithPresets({ onDataImport, onFileSelected }) {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return '開催日未定';
    const y = dateStr.substring(0, 4);
    const m = dateStr.substring(4, 6);
    const d = dateStr.substring(6, 8);
    return `${y}/${m}/${d}`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
      setSelectedPreset(null);
      const reader = new FileReader();
      reader.onload = (e) => { if (onDataImport) onDataImport(e.target.result, file.name); };
      reader.readAsText(file);
    }
    if (onFileSelected) onFileSelected();
  };

  const fetchPreset = async (path, label, presetObj) => {
    try {
      const res = await fetch(path, { cache: 'no-store' });
      const text = await res.text();
      setTimeout(() => { if (onDataImport) onDataImport(text, presetObj.id); }, 0);
      setSelectedFileName(label);
      setSelectedPreset(presetObj);
      if (onFileSelected) onFileSelected();
    } catch (err) {
      console.error('読み込み失敗:', err);
    }
  };

  // R3.0.3 地域フィルター追加
  const [regionFilter, setRegionFilter] = useState("domestic"); 

  // ★ 英語版から来たら海外をデフォルトにする
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") === "en") {
      setRegionFilter("overseas");
    }
  }, []);

  // R2.4.2 ソート処理追加
  const [sortKey, setSortKey] = useState("date");// date or effort
  const [sortOrder, setSortOrder] = useState("asc"); // asc or desc

  const sortedPresets = [...GPX_PRESETS].sort((a, b) => {
    let result = 0;

    if (sortKey === "date") {
      result = Number(a.date) - Number(b.date); 
    }

    if (sortKey === "effort") {
      const ea = getKmEffort(a.distance, a.elevation);
      const eb = getKmEffort(b.distance, b.elevation);
      result = ea - eb;
    }

    return sortOrder === "asc" ? result : -result;
  });

  // R3.0.3 地域フィルター処理
  const filteredPresets = sortedPresets.filter(preset => {
    if (regionFilter === "all") return true;
    return preset.region === regionFilter;
  });

  // ▼ 今日以降のレースと過去のレースに分割（YYYYMMDD形式で比較）
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
  const currentDate = String(now.getDate()).padStart(2, '0');
  const todayStr = `${currentYear}${currentMonth}${currentDate}`;

  const upcomingPresets = filteredPresets.filter(preset => {
    if (!preset.date || preset.date.length !== 8) return false;
    return preset.date >= todayStr;
  });

  const pastPresets = filteredPresets.filter(preset => {
    if (!preset.date || preset.date.length !== 8) return true;
    return preset.date < todayStr;
  });

  // 追加表示（過去のレースなど）の件数カウンター
  const [extraCount, setExtraCount] = useState(0);

  // フィルターやソートが変更されたら追加表示分をリセット
  useEffect(() => {
    setExtraCount(0);
  }, [regionFilter, sortKey, sortOrder]);

  const loadMore = () => {
    setExtraCount(prev => prev + 6);
  };

  // 今日以降のレースは最初から全件表示し、さらに過去のレースを追加分（+6件ずつ）表示する
  const visiblePastPresets = pastPresets.slice(0, extraCount);
  const visiblePresets = upcomingPresets.length > 0 
    ? [...upcomingPresets, ...visiblePastPresets] 
    : filteredPresets.slice(0, 9 + extraCount); // 今日以降が一件もない場合の安全策

  const [showGpx, setShowGpx] = useState(false);

  return (
    <>
      {selectedFileName && (
        <>
          <div style={styles.fixedSelectionBar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
              <span style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>選択中:</span>
              <strong style={{
                fontSize: '0.85rem',
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {selectedFileName}
              </strong>
            </div>
            {selectedPreset?.url && (
              <a href={selectedPreset.url} target="_blank" rel="noopener noreferrer"
                style={{
                  color: '#fff',
                  fontSize: '0.7rem',
                  textDecoration: 'underline',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '4px 8px',
                  borderRadius: '20px',
                  flexShrink: 0,
                  marginLeft: '8px'
                }}>
                公式サイト
              </a>
            )}
          </div>
          <div style={styles.spacer} />
        </>
      )}
      <div style={styles.container}>
        <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: '#4a5568',
            textAlign: 'center',
            marginBottom: '12px'
        }}>
          レースを選択してください
          <Tooltip
            text={`■ km-effortとは？
  距離（km）と累積標高（m）から算出される、レースの
  総合的な負荷を表す国際指標です。

  【計算式】
  km-effort = 距離（km） + 累積標高 ÷ 100

  【ITRA 7段階分類】
  0〜24：XXS（超初級）
  25〜44：XS（初級）
  45〜74：S（中級）
  75〜114：M（中上級）
  115〜154：L（上級）
  155〜209：XL（超上級）
  210以上：XXL（ウルトラ級）

  ────────────────────
  ■ Finisher Levelとは？
  ITRA パフォーマンスインデックス（PI）を用いて、
  「制限時間ぎりぎりで完走するために最低限必要な走力」
  を推定した値です。

  ・自分の PI と比較することで、そのレースを完走できる
  走力があるかの目安になります。
  ・レースの“完走難易度”を客観的に把握できます。

  `}
          />
        </h2>

        {/* ソートUI */}
        <div style={{
          display: "flex",
          gap: "12px",
          marginBottom: "16px",
          marginTop: "8px"
        }}>
          <span
            onClick={() => setSortKey("date")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: sortKey === "date" ? "rgba(255,183,77,0.25)" : "transparent",
              color: sortKey === "date" ? "#f57c00" : "#718096",
              fontWeight: sortKey === "date" ? 600 : 500,
              fontSize: "0.8rem",
              transition: "all 0.2s ease"
            }}
          >
            日付順
          </span>

          <span
            onClick={() => setSortKey("effort")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: sortKey === "effort" ? "rgba(255,183,77,0.25)" : "transparent",
              color: sortKey === "effort" ? "#f57c00" : "#718096",
              fontWeight: sortKey === "effort" ? 600 : 500,
              fontSize: "0.8rem",
              transition: "all 0.2s ease"
            }}
          >
            km-effort順
          </span>

          <span
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: "rgba(0,0,0,0.05)",
              color: "#555",
              fontWeight: 500,
              fontSize: "0.8rem",
              transition: "all 0.2s ease"
            }}
          >
            {sortOrder === "asc" ? "昇順 ↑" : "降順 ↓"}
          </span>

        </div>

        {/* 国内/海外フィルタ */}
        <div style={{
          display: "flex",
          gap: "12px",
          marginBottom: "16px",
          marginTop: "4px"
        }}>
          <span
            onClick={() => setRegionFilter("all")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: regionFilter === "all" ? "rgba(255,183,77,0.25)" : "transparent",
              color: regionFilter === "all" ? "#f57c00" : "#718096",
              fontWeight: regionFilter === "all" ? 600 : 500,
              fontSize: "0.8rem"
            }}
          >
            全て
          </span>

          <span
            onClick={() => setRegionFilter("domestic")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: regionFilter === "domestic" ? "rgba(255,183,77,0.25)" : "transparent",
              color: regionFilter === "domestic" ? "#f57c00" : "#718096",
              fontWeight: regionFilter === "domestic" ? 600 : 500,
              fontSize: "0.8rem"
            }}
          >
            国内
          </span>

          <span
            onClick={() => setRegionFilter("overseas")}
            style={{
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "12px",
              background: regionFilter === "overseas" ? "rgba(255,183,77,0.25)" : "transparent",
              color: regionFilter === "overseas" ? "#f57c00" : "#718096",
              fontWeight: regionFilter === "overseas" ? 600 : 500,
              fontSize: "0.8rem"
            }}
          >
            海外
          </span>
        </div>

        <ul style={styles.grid}>
          {visiblePresets.map((preset) => {
            const isSelected = selectedPreset?.id === preset.id;

            const { code, jp } = getCategoryHybrid(preset.distance, preset.elevation);
            const badgeColor = getCategoryBadgeColor(code, isSelected);
            const kmEffort = getKmEffort(preset.distance, preset.elevation);

            return (
              <li key={preset.id}>
                <button
                  onClick={() => fetchPreset(preset.file, preset.label, preset)}
                  style={{
                    ...styles.card,
                    borderColor: isSelected ? '#f57c00' : '#cbd5e1',
                    borderWidth: isSelected ? '3px' : '2px',
                    boxShadow: isSelected
                      ? "0 6px 14px rgba(0,0,0,0.18)"
                      : "0 1px 2px rgba(0,0,0,0.05)",
                    transform: isSelected ? "translateY(-3px) scale(1.03)" : "none",
                  }}
                >

                  {/* 日付バッジ */}
                  <div style={{
                    ...styles.badgeBase,
                    left: 0,
                    fontSize: "1.0rem", 
                    backgroundColor: "rgba(255,255,255,0.25)",
                    color: "#333",
                    border: "1px solid rgba(255,255,255,0.4)",
                    backdropFilter: "blur(6px)"
                  }}>
                    {formatDate(preset.date)}
                  </div>

                  {/* 国内 / 海外 バッジ */}
                  <div
                    style={{
                      ...styles.badgeBase,
                      left: "auto",
                      fontSize: "0.6em", 
                      right: 0,
                      top: "auto",
                      bottom: 0,
                      borderTopLeftRadius: "10px",
                      borderBottomRightRadius: 0,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: preset.region === "domestic" ? "#2e7d32" : "#1565c0",
                      border: "1px solid rgba(255,255,255,0.4)",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    {preset.region === "domestic" ? "国内" : "海外"}
                  </div>

                  {/* カテゴリバッジ */}
                  <div style={{
                    ...styles.badgeBase,
                    right: '0',
                    backgroundColor: badgeColor,
                    color: '#fff'
                  }}>
                    {code}（{jp}）
                  </div>


                  {isSelected && (
                    <div style={{
                      color: '#f57c00',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      marginTop: '6px',
                      marginBottom: '0px'
                    }}>
                      ✓ 選択済み
                    </div>
                  )}
                  <strong style={{
                    fontSize: '1.3rem',          
                    fontWeight: 800,                  
                    padding: '6px 10px',              
                    borderRadius: '6px',              
                    display: 'inline-block',          
                    marginTop: '14px',
                    marginBottom: '8px',
                    color: isSelected ? '#d65f00' : '#2d3748',
                  }}>
                    {preset.label}
                  </strong>
                  {/* specs */}
                  <div style={styles.specs}>
                    <span style={styles.specItem}>🏃 {preset.distance}km</span>
                    <span style={styles.specItem}>⛰ {preset.elevation}m</span>
                    <span style={styles.specItem}>⏱️ 制限 {preset.limit}</span>
                    <span style={styles.specItem}>⚡ km-effort: {Math.round(kmEffort)} </span>
                    {preset.finisherLevel && (
                      <span style={styles.specItem}>🎖️ Finisher Level: {preset.finisherLevel}PI</span>
                    )}
                  </div>

                </button>
              </li>
            );
          })}
        </ul>

        {/* 過去レースを見る / 続きを見るボタンの切り替え */}
        {visiblePresets.length < filteredPresets.length && (
          <button
            onClick={loadMore}
            style={{
              margin: "28px auto 0",
              display: "block",
              padding: "10px 0",
              background: "transparent",
              border: "none",
              color: "#2c7be5",
              fontSize: "1.5rem",
              fontWeight: 600,
              letterSpacing: "0.02em",
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.25s ease",
              opacity: 0.85
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.85";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {extraCount === 0 ? "過去のレースを見る ↓" : "続きを見る ↓"}
          </button>
        )}

        {/* 独自GPXインポート */}
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
          <details
            open={showGpx}
            onToggle={(e) => setShowGpx(e.target.open)}
            style={{ width: 'fit-content', maxWidth: '90%' }}
          >
            <summary
              style={{
                listStyle: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontSize: '1.0rem',
                fontWeight: 600,
                color: '#4a5568b4',
                cursor: 'pointer',
                padding: '6px 0',
                position: 'relative',
                paddingRight: '18px'
              }}
            >
              上記以外の独自GPXをインポートする

              <span
                style={{
                  position: 'absolute',
                  right: 0,
                  fontSize: '0.8rem',
                  color: '#a0aec0'
                }}
              >
                {showGpx ? "▲" : "▼"}
              </span>
            </summary>

            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <input
                id="gpx-upload"
                type="file"
                accept=".gpx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <label
                htmlFor="gpx-upload"
                style={{
                  padding: '6px 14px',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  color: '#4a5568b4',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📁 ファイルを選択
              </label>
            </div>
          </details>
        </div>
      </div>
    </>
  );
}

export default GPSImportWithPresets;